import { ColumnDefinition } from '../models/column-definition.model';

export const REVIEW_STATUS_COLUMN_FIELD = 'review_status';
export const SUPPLIER_NAME_COLUMN_FIELD = 'supplier_name';
export const SUBMISSION_STATUS_COLUMN_FIELD = 'submission_status';
export const EVIDENCE_COUNT_COLUMN_FIELD = 'evidence_count';
// The columns plot linking (AGD-834 v1) adds to the plantation list: the
// seller-side Submission and the buyer-side Supplier. When each shows is
// decided in PlantationTableComponent.
export const PLOT_LINKING_COLUMN_FIELDS: readonly string[] = [
  SUBMISSION_STATUS_COLUMN_FIELD,
  SUPPLIER_NAME_COLUMN_FIELD
];
// Not user-configurable; Submission shows only when the dataset can submit.
export const ALWAYS_VISIBLE_COLUMN_FIELDS = [
  'actions',
  REVIEW_STATUS_COLUMN_FIELD,
  SUBMISSION_STATUS_COLUMN_FIELD
] as const;

export function isPlotLinkingColumn(field: string): boolean {
  return PLOT_LINKING_COLUMN_FIELDS.includes(field);
}

// New columns start hidden on datasets with saved settings, except these.
export const DEFAULT_VISIBLE_NEW_COLUMN_FIELDS: readonly string[] = [
  'plot_history',
  SUPPLIER_NAME_COLUMN_FIELD,
  EVIDENCE_COUNT_COLUMN_FIELD
];

export function isDefaultVisibleNewColumn(field: string): boolean {
  return DEFAULT_VISIBLE_NEW_COLUMN_FIELDS.includes(field);
}

export function isAlwaysVisibleColumn(field: string): boolean {
  return ALWAYS_VISIBLE_COLUMN_FIELDS.includes(
    field as (typeof ALWAYS_VISIBLE_COLUMN_FIELDS)[number]
  );
}

export const PLANTATION_LIST_TABLE_COLUMNS: ColumnDefinition[] = [
  {
    id: 1,
    field: 'plantation_name',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.PLANTATION_NAME',
    sortable: true,
    editable: true,
    width: '15rem'
  },
  {
    id: 2,
    field: 'plantation_code',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.PLANTATION_CODE',
    sortable: true,
    editable: true,
    width: '15rem'
  },
  {
    id: 23,
    field: SUPPLIER_NAME_COLUMN_FIELD,
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.SUPPLIER',
    sortable: true,
    editable: false,
    width: '15rem'
  },
  {
    id: 19,
    field: 'plot_history',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.PLOT_HISTORY',
    sortable: false,
    editable: false,
    width: '10rem'
  },
  {
    id: 20,
    field: REVIEW_STATUS_COLUMN_FIELD,
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.REVIEW',
    sortable: false,
    editable: false,
    width: '7rem',
    style: 'text-align: center'
  },
  {
    id: 22,
    field: SUBMISSION_STATUS_COLUMN_FIELD,
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.SUBMISSION',
    sortable: false,
    editable: false,
    width: '11rem'
  },
  {
    id: 3,
    field: 'risk_rating',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.RISK_RATING',
    editable: false,
    width: '12rem'
  },
  {
    id: 4,
    field: 'mitigation_status',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.MITIGATION_STATUS',
    editable: false,
    width: '15rem'
  },
  {
    id: 24,
    field: EVIDENCE_COUNT_COLUMN_FIELD,
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.EVIDENCE',
    sortable: false,
    editable: false,
    width: '8rem',
    style: 'text-align: center'
  },
  {
    id: 5,
    field: 'risks',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.GEOSPATIAL',
    editable: false,
    width: '20rem'
  },
  {
    id: 20,
    field: 'geometry_type',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.GEOMETRY_TYPE',
    sortable: false,
    editable: false,
    width: '13rem'
  },
  {
    id: 6,
    field: 'average_monthly_yield',
    numeric: true,
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.AVERAGE_YIELD',
    editable: false,
    width: '11rem',
    sortable: true
  },
  {
    id: 7,
    field: 'planting_year',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.PLANTING_YEAR',
    width: '11rem',
    sortable: true
  },
  {
    id: 8,
    field: 'area_calculated',
    numeric: true,
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.LAND_AREA',
    sortable: true,
    editable: true,
    width: '12rem'
  },
  {
    id: 9,
    field: 'country',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.COUNTRY',
    sortable: true,
    width: '12rem'
  },
  {
    id: 10,
    field: 'adm_1',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.PROVINCE_STATE',
    sortable: true,
    width: '13rem'
  },
  {
    id: 11,
    field: 'adm_2',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.DISTRICT',
    sortable: true,
    width: '12rem'
  },
  {
    id: 12,
    field: 'adm_3',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.SUB_DISTRICT',
    sortable: true,
    width: '12rem'
  },
  {
    id: 13,
    field: 'adm_4',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.VILLAGE',
    sortable: true,
    width: '12rem'
  },
  {
    id: 14,
    field: 'date_created',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.DATE_CREATED',
    sortable: true,
    editable: true,
    width: '12rem'
  },
  {
    id: 15,
    field: 'data_source',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.DATA_SOURCE',
    sortable: true,
    editable: false,
    width: '15rem'
  },
  {
    id: 16,
    field: 'field_team_code',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.PROJECT_CODE',
    sortable: false,
    editable: false,
    width: '12rem'
  },
  {
    id: 17,
    field: '_createdbyname',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.CREATED_BY',
    sortable: false,
    editable: true,
    width: '12rem'
  },
  {
    id: 18,
    field: 'actions',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.ACTIONS',
    sortable: false,
    editable: false,
    width: '9rem'
  }
];

/** The only plantation columns the SI linking flow shows; new dashboard columns stay out unless added here. */
export const SI_LINKING_COLUMN_FIELDS: readonly string[] = [
  'plantation_name',
  'plantation_code',
  'risk_rating',
  'mitigation_status',
  'risks',
  'area_calculated',
  'country',
  'adm_1',
  'adm_2',
  'adm_3',
  'adm_4'
];

export function isSiLinkingColumn(field: string): boolean {
  return SI_LINKING_COLUMN_FIELDS.includes(field);
}

export const SI_LINKING_TABLE_COLUMNS: ColumnDefinition[] =
  PLANTATION_LIST_TABLE_COLUMNS.filter((column) =>
    isSiLinkingColumn(column.field)
  );

export const LEGALITY_QUESTIONNAIRE_TABLE_COLUMNS: ColumnDefinition[] = [
  {
    id: 1,
    field: 'plantation_name',
    displayName: 'DASHBOARD.QUESTIONNAIRE_COLUMNS.PLANTATION_NAME',
    sortable: true,
    filterApiKey: '_geo_type',
    editable: false,
    isText: true,
    width: '15rem'
  },
  {
    id: 2,
    field: 'plantation_code',
    displayName: 'DASHBOARD.QUESTIONNAIRE_COLUMNS.PLANTATION_CODE',
    sortable: true,
    editable: false,
    isText: true,
    width: '15rem'
  },
  {
    id: 3,
    field: 'risk_rating',
    displayName: 'DASHBOARD.QUESTIONNAIRE_COLUMNS.RISK_RATING',
    sortable: false,
    filterApiKey: 'risk_rating',
    editable: false,
    isText: true,
    width: '12rem'
  },
  {
    id: 4,
    field: 'mitigation_status',
    displayName: 'DASHBOARD.QUESTIONNAIRE_COLUMNS.MITIGATION_STATUS',
    editable: false,
    isText: true,
    width: '15rem'
  },
  {
    id: 5,
    field: 'overAllSurveyMaxRiskRatingProps.risk_framework',
    displayName: 'DASHBOARD.QUESTIONNAIRE_COLUMNS.RISK_FRAMEWORK',
    sortable: false,
    filterApiKey: 'risk_framework',
    editable: false,
    isText: true,
    width: '15rem'
  },
  {
    id: 6,
    field: 'land_legality',
    displayName: 'DASHBOARD.QUESTIONNAIRE_COLUMNS.LAND_USE_RIGHTS',
    sortable: false,
    filterApiKey: 'risks',
    isText: false,
    editable: false,
    width: '12rem'
  },
  {
    id: 7,
    field: 'environmental_protection',
    displayName: 'DASHBOARD.QUESTIONNAIRE_COLUMNS.ENVIRONMENTAL_PROTECTION',
    sortable: false,
    filterApiKey: 'risks',
    isText: false,
    editable: false,
    width: '16rem'
  },
  {
    id: 8,
    field: 'third_parties_FPIC',
    displayName:
      'DASHBOARD.QUESTIONNAIRE_COLUMNS.THIRD_PARTIES_RIGHTS_AND_FPIC',
    sortable: false,
    filterApiKey: 'risks',
    isText: false,
    editable: false,
    width: '18rem'
  },
  {
    id: 9,
    field: 'labour_rights',
    displayName: 'DASHBOARD.QUESTIONNAIRE_COLUMNS.LABOUR_HUMAN_RIGHTS',
    sortable: false,
    filterApiKey: 'risks',
    isText: false,
    editable: false,
    width: '13rem'
  },
  {
    id: 10,
    field: 'tax_anti_corruption',
    displayName: 'DASHBOARD.QUESTIONNAIRE_COLUMNS.TAX_ANTI_CORRUPTION',
    sortable: false,
    filterApiKey: 'risks',
    isText: false,
    editable: false,
    width: '15rem'
  },
  {
    id: 11,
    field: 'data_source',
    displayName: 'DASHBOARD.QUESTIONNAIRE_COLUMNS.DATA_SOURCE',
    sortable: true,
    filterApiKey: 'data_source',
    isText: true,
    editable: false,
    width: '15rem'
  },
  {
    id: 12,
    field: 'country',
    displayName: 'DASHBOARD.QUESTIONNAIRE_COLUMNS.COUNTRY',
    sortable: true,
    filterApiKey: 'country',
    isText: true,
    editable: false,
    width: '12rem'
  },
  {
    id: 13,
    field: 'actions',
    displayName: 'DASHBOARD.PLANTATION_LIST_COLUMNS.ACTIONS',
    sortable: false,
    editable: false,
    width: '9rem'
  }
];

export const SURVEY_RISKS_COLUMNS: ColumnDefinition[] = [
  {
    id: 1,
    field: 'land_legality',
    displayName: 'DASHBOARD.QUESTIONNAIRE_COLUMNS.LAND_USE_RIGHTS',
    sortable: false,
    filter: false,
    isText: false,
    editable: false,
    style: 'min-width: 17.4%'
  },
  {
    id: 2,
    field: 'environmental_protection',
    displayName: 'DASHBOARD.QUESTIONNAIRE_COLUMNS.ENVIRONMENTAL_PROTECTION',
    sortable: false,
    filter: false,
    isText: false,
    editable: false,
    style: 'min-width: 17.4%'
  },
  {
    id: 3,
    field: 'third_parties_FPIC',
    displayName:
      'DASHBOARD.QUESTIONNAIRE_COLUMNS.THIRD_PARTIES_RIGHTS_AND_FPIC',
    sortable: false,
    filter: false,
    isText: false,
    editable: false,
    style: 'min-width: 17.4%'
  },
  {
    id: 4,
    field: 'labour_rights',
    displayName: 'DASHBOARD.QUESTIONNAIRE_COLUMNS.LABOUR_HUMAN_RIGHTS',
    sortable: false,
    filter: false,
    isText: false,
    editable: false,
    style: 'min-width: 17.4%'
  },
  {
    id: 5,
    field: 'tax_anti_corruption',
    displayName: 'DASHBOARD.QUESTIONNAIRE_COLUMNS.TAX_ANTI_CORRUPTION',
    sortable: false,
    filter: false,
    isText: false,
    editable: false,
    style: 'min-width: 17.4%'
  }
];

export const RISK_VERIFICATION_COLUMNS: ColumnDefinition[] = [
  {
    id: 1,
    field: 'risk_display_name',
    displayName: 'DASHBOARD.RISK_VERIFICATION_MODAL.COLUMNS.RISK_IDENTIFIED'
  },
  {
    id: 2,
    field: 'risk_type',
    displayName: 'DASHBOARD.RISK_VERIFICATION_MODAL.COLUMNS.RISK_TYPE'
  },
  {
    id: 3,
    field: 'status',
    displayName: 'DASHBOARD.RISK_VERIFICATION_MODAL.COLUMNS.RISK_STATUS'
  },
  {
    id: 4,
    field: 'file_type',
    displayName: 'DASHBOARD.RISK_VERIFICATION_MODAL.COLUMNS.FILE_TYPE',
    style: 'min-width: 10rem'
  },
  {
    id: 5,
    field: 'updated_date',
    displayName: 'DASHBOARD.RISK_VERIFICATION_MODAL.COLUMNS.UPDATED_DATE'
  }
];

export const AGRITRACE_FILE_COLUMNS: ColumnDefinition[] = [
  {
    id: 1,
    field: 'file_name',
    displayName:
      'DASHBOARD.RISK_VERIFICATION_MODAL.AGRITRACE.COLUMNS.AGRITRACE_FILE_NAME',
    style: 'word-break: break-word; width: 15rem;'
  },
  {
    id: 2,
    field: 'category',
    displayName: 'DASHBOARD.RISK_VERIFICATION_MODAL.AGRITRACE.COLUMNS.CATEGORY'
  },
  {
    id: 3,
    field: 'created_at',
    displayName:
      'DASHBOARD.RISK_VERIFICATION_MODAL.AGRITRACE.COLUMNS.CREATED_DATE'
  },
  {
    id: 4,
    field: 'preview',
    displayName: 'DASHBOARD.RISK_VERIFICATION_MODAL.AGRITRACE.COLUMNS.PREVIEW'
  }
];

export const MANAGE_FAVORITE_FILTER_COLUMNS = [
  {
    id: 1,
    field: 'filter_name',
    displayName:
      'DASHBOARD.MANAGE_FAVORITE_FILTER_MODAL.COLUMNS.FAVORITE_FILTER_NAME'
  },
  {
    id: 2,
    field: 'actions',
    displayName: 'DASHBOARD.MANAGE_FAVORITE_FILTER_MODAL.COLUMNS.ACTIONS'
  }
];

export const RISK_OVERLAP_DETAILS_COLUMNS: ColumnDefinition[] = [
  {
    id: 1,
    field: 'percentage',
    numeric: true,
    alignEnd: true,
    displayName: 'MAP_PAGE.PERCENTAGE',
    sortable: true,
    width: '10rem'
  },
  {
    id: 2,
    field: 'area',
    numeric: true,
    alignEnd: true,
    displayName: 'MAP_PAGE.HECTARES',
    sortable: true,
    width: '7rem'
  },
  {
    id: 3,
    field: 'plantation_name',
    displayName: 'MAP_PAGE.PLANTATION',
    sortable: true
  }
];

export const EDIT_TABLE_COLUMNS_COLUMNS: ColumnDefinition[] = [
  {
    displayName: 'EDITABLE_TABLE_COLUMNS_MODAL.TABLE_COLUMN_NAME',
    field: 'displayName',
    id: 1
  }
];

export const RISK_RATING_SQL =
  'IF(mitigation_status = "self-verified", "C", IF(IFNULL((SELECT MAX(risk_score) FROM UNNEST(survey_risks)), 9) = 9 AND country = "Invalid Geometry", "ND",(IF(IFNULL((SELECT MAX(risk_score) FROM UNNEST(survey_risks)), 9) >= 9 OR country = "Invalid Geometry", "PD", (IF(IFNULL((SELECT MAX(risk_score) FROM UNNEST(survey_risks)), 9) = 1 OR has_any_risk_value, "NC", "C"))))))';
